/**
 * Sprites standing in the world's shade (sun shadows of buildings, trees and entity tiles) are darkened
 * like the ground there. The shade layer is drawn under every sprite, so without this a table, a bush or
 * a wall stood in a tower's shadow stayed lit on a dark floor. Pure: no pixi.
 *
 * A sprite is asked about one point: the ground just SUNWARD of where it stands. Its own foot is no good
 * -- everything that casts stands inside its own shadow there -- and if the light is blocked before it
 * reaches the sprite, the ground on that side is in the same shade. Only low art takes it whole: a tree's
 * crown stands above most of what shades its trunk, so tall art is shaded only as high as the buildings'
 * shade climbs it (shadeClimb, drawn by MapRenderingManager's shade overlays).
 */

/** Art taller than this is shaded only as high as the shade climbs it (shadeClimb), not whole. */
export const SHADED_ART_MAX_HEIGHT = 96

/** The unit vector toward the sun on the ground (against the shadows' direction). */
export const towardSun = (sun: { x: number; y: number }): { x: number; y: number } => {
    const length = Math.hypot(sun.x, sun.y) || 1
    return { x: -sun.x / length, y: -sun.y / length }
}

/**
 * Where a wall piece (any piece of the wall standing on cell `cx, cy`) samples the shade: the sunward
 * quarter of its own cell, clear of its own footprint and its own shadow, which both lie south-west -- or, under
 * a light from the north-west (the moon), south-east, and it samples the north-west quarter.
 */
export const wallShadePoint = (cx: number, cy: number, sun?: { x: number; y: number }): { x: number; y: number } =>
    ({ x: cx * 32 + ((sun?.x ?? -1) > 0 ? 6 : 26), y: cy * 32 + 14 })

/**
 * Whether the wall piece standing on cell `cx, cy` is in shade, given the room's shade test
 * (building_shade.inBuildingShade). Only the BUILDINGS' shade counts. A piece is tinted whole by this one
 * point, which suits a building's shadow -- broad, and edged like the walls themselves -- but not a plant's:
 * a tree's crown throws a narrow ragged band, and one crossing a piece's sample point darkened that piece
 * alone, a hard-edged dark block between lit neighbours (a cabin corner under a tree to its north-east).
 * The plants' shade still falls on the ground along the wall.
 */
export const wallInShade = (cx: number, cy: number,
    inShade: (x: number, y: number, buildingsOnly: boolean) => boolean, sun?: { x: number; y: number }): boolean => {
    const p = wallShadePoint(cx, cy, sun)
    return inShade(p.x, p.y, true)
}

/** Where a piece of furniture standing on cell `cx, cy` samples the shade: across its foot (it casts no world shadow of its own). */
export const furnitureShadePoints = (cx: number, cy: number): { x: number; y: number }[] =>
    [8, 16, 24].map(dx => ({ x: cx * 32 + dx, y: cy * 32 + 24 }))

/**
 * Where art drawn over `x, y, width, height` (its foot on the bottom edge) samples the shade, or undefined
 * when it is too tall to take it: off its foot toward the sun by about half its width, past its own shadow,
 * at five points across its width -- art takes the share of them that is shaded, so a shadow's edge grades
 * across a clump of grass instead of cutting it into lit and dark blocks. Art that casts no shadow of its
 * own (`casts` false) samples a patch round its own foot instead.
 */
export const artShadePoints = (x: number, y: number, width: number, height: number, sun: { x: number; y: number }, casts = true) => {
    if (height > SHADED_ART_MAX_HEIGHT) return undefined
    // Art that casts nothing (grass, flowers) looks at the ground right under it, where the shade is drawn.
    const toward = towardSun(sun), reach = casts ? Math.max(10, width * 0.45) : 0
    const cx = x + width / 2 + toward.x * reach, cy = y + height - 4 + toward.y * reach
    const across = [-0.4, -0.2, 0, 0.2, 0.4].map(f => cx + f * width)
    if (casts) return across.map(px => ({ x: px, y: cy }))
    // Rows of grass overlap: three rows of points, so neighbouring rows read close shares and a shadow's
    // edge spreads over several of them instead of drawing a straight line between two.
    return [-16, 0, 16].flatMap(dy => across.map(px => ({ x: px, y: cy + dy })))
}

/**
 * How high up tall art (a tree) standing at `foot` the buildings' shade climbs, in px above the foot, up to
 * `height`. A point h up it is in shade when something blocks its sun; for walls, standing on the ground,
 * that is when the ground h along the shadows' way from the foot is still in their shade, all the way from
 * the foot. Only the buildings' shade counts: the art's own shadow (and its neighbours', in a forest) runs
 * that way from the foot too.
 */
export const shadeClimb = (foot: { x: number; y: number }, height: number, sun: { x: number; y: number },
    inShade: (x: number, y: number) => boolean, step = 4): number => {
    if (!inShade(foot.x, foot.y)) return 0
    for (let h = step; h <= height; h += step) {
        if (!inShade(foot.x + sun.x * h, foot.y + sun.y * h)) return h - step / 2
    }
    return height
}

/**
 * Where a body's own shadow lies, to ask whether it falls in shade already (where it adds nothing): along
 * it from the soles, a quarter of the body's `height` apart, out to its tip.
 */
export const castShadePoints = (soles: { x: number; y: number }, height: number, sun: { x: number; y: number }): { x: number; y: number }[] =>
    [0.25, 0.5, 0.75, 1].map(f => ({ x: soles.x + sun.x * height * f, y: soles.y + sun.y * height * f }))

/** Two tints combined, as drawing through both would: channel by channel, multiplied. */
export const multiplyTints = (a: number, b: number): number => {
    const ch = (shift: number) => Math.round((((a >> shift) & 0xff) * ((b >> shift) & 0xff)) / 255)
    return (ch(16) << 16) | (ch(8) << 8) | ch(0)
}
